import { Alert, Descriptions, Form, Input, Modal, Tooltip } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import type { OperationResult } from '@/shared/api/operationResult'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { REASON_MAX } from './hrApi'
import { approveLeave, cancelLeave, rejectLeave, type LeaveRequest } from './leaveApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

/** Uchta amal, uchtasining qoidasi boshqa — javadoc'ga qarang */
export type LeaveAction = 'APPROVE' | 'REJECT' | 'CANCEL'

interface Props {
  action: LeaveAction
  request: LeaveRequest
  onClose: () => void
  onDone: (result: OperationResult) => void
}

interface FormValues {
  note?: string
}

/**
 * Ta'til so'rovi bo'yicha qaror.
 *
 * <h2>⚠️ UCHTA AMAL, BITTA OYNA — LEKIN QOIDALARI BOSHQA</h2>
 * <ul>
 *   <li><b>Tasdiqlash</b> — izoh IXTIYORIY. Balans yetmasa server 409
 *       qaytaradi va sabab shu oynada o'qiladi: kutayotgan kunlar
 *       qoldiqqa KIRMAYDI, ya'ni «qoldiq 10» ko'rinib turib rad
 *       javob kelishi mumkin.</li>
 *   <li><b>Rad etish</b> — izoh MAJBURIY (`LeaveDecision.requireNote()`
 *       → 400). Sababsiz rad etish xodimga hech narsa aytmasdi.</li>
 *   <li><b>Bekor qilish</b> — tanasi YO'Q va huquqi boshqa
 *       (`hr.leave.request`, `hr.leave.approve` emas): bu xodimning
 *       o'z so'rovini qaytarib olish yo'li. Tasdiqlangan ta'til ham
 *       bekor qilinadi va kunlar balansga qaytadi.</li>
 * </ul>
 *
 * <h2>⚠️ SO'ROVNI TAHRIRLAB BO'LMAYDI</h2>
 * Sana, tur va sabab bu yerda o'zgartirilmaydi — backendda bunday
 * maydonlar umuman yo'q. Aks holda «men boshqa narsani ko'rgan edim»
 * degan holat paydo bo'lardi (`StockApprovalDecisionModal` bilan bir
 * xil mulohaza).
 *
 * <h2>⚠️ XATO OYNANING ICHIDA (`guards.mjs` 1-bo'limi)</h2>
 * `onError` YO'Q: 400 («rad etish sababi majburiy») va 409 («balans
 * yetarli emas», «holat o'zgargan») aynan shu yerda o'qilishi kerak.
 * Yopishda `decide.reset()`.
 */
export function LeaveDecisionModal({ action, request, onClose, onDone }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  // ⚠️ N4: `requested_by` — aktyor identifikatori (`auth.getName()`),
  //    xodim ismi EMAS. Qaror oynasi jadval bilan BIR XIL matnni
  //    ko'rsatishi shart: bir joyda nom, ikkinchisida UUID chiqsa
  //    tasdiqlovchi ikkalasini bir odam deb tanimasligi mumkin.
  const actors = useActorDirectory()

  const decide = useMutation({
    mutationFn: (values: FormValues): Promise<OperationResult> => {
      const note = values.note?.trim() ? values.note.trim() : null
      if (action === 'APPROVE') return approveLeave(request.id, note)
      if (action === 'REJECT') return rejectLeave(request.id, note ?? '')
      return cancelLeave(request.id)
    },
    onSuccess: (result) => {
      // ⚠️ `hr` PREFIKSI: qaror balansni ham o'zgartiradi, ro'yxatni ham
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onDone(result)
      onClose()
    },
  })

  const close = () => {
    decide.reset()
    onClose()
  }

  return (
    <Modal
      open
      title={t(`hr.leave.action.${action}.title`)}
      onCancel={close}
      onOk={() =>
        action === 'CANCEL'
          ? decide.mutate({})
          : submitOnValid(form, (values) => decide.mutate(values))
      }
      confirmLoading={decide.isPending}
      okText={t(`hr.leave.action.${action}.submit`)}
      okButtonProps={{ danger: action !== 'APPROVE' }}
      cancelText={action === 'CANCEL' ? t('common.back') : t('common.cancel')}
      destroyOnHidden
      width={620}
    >
      <Alert
        type={action === 'APPROVE' ? 'info' : 'warning'}
        showIcon
        style={{ marginBottom: 16 }}
        message={t(`hr.leave.action.${action}.note`)}
      />

      {decide.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(decide.error, t)}
        />
      ) : null}

      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('hr.leave.person')}>
          {request.fullName ?? '—'}
          {request.personnelNo === null ? '' : ` · ${request.personnelNo}`}
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.leave.type')}>
          {request.leaveTypeName ?? request.leaveTypeCode ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.leave.range')}>
          {`${request.startsOn} … ${request.endsOn}`}
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.leave.days')}>{request.days}</Descriptions.Item>
        <Descriptions.Item label={t('hr.leave.reason')}>
          {request.reason === null ? '—' : <ExpandableHint text={request.reason} fontSize={14} />}
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.leave.requestedBy')}>
          {actorLabel(request.requestedBy, actors, t('common.dash'))}
        </Descriptions.Item>
        <Descriptions.Item label={t('hr.leave.requestedAt')}>
          {request.requestedAt === null ? (
            '—'
          ) : (
            <Tooltip title={utcTooltip(request.requestedAt)}>
              <span>{formatDateTime(request.requestedAt)}</span>
            </Tooltip>
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* ⚠️ Bekor qilishda maydon CHIZILMAYDI: backend tanasiz `POST`.
          Maydonni ko'rsatib qiymatni tashlab yuborish «yozdim —
          saqlanmadi» degan yolg'on bo'lardi. */}
      {action === 'CANCEL' ? null : (
        <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={decide.isPending}>
          <Form.Item
            name="note"
            label={t('hr.leave.decisionNote')}
            extra={
              action === 'REJECT' ? t('hr.leave.noteRejectHint') : t('hr.leave.noteApproveHint')
            }
            rules={[
              ...(action === 'REJECT'
                ? [{ required: true, message: t('hr.leave.validation.rejectNoteRequired') }]
                : []),
              { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
            ]}
          >
            <Input.TextArea rows={2} autoFocus />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
